import {useEffect, useRef, useState} from 'react';
import {ArrowLeft, Check, Copy, Download, ExternalLink, FileText, Receipt, Wallet} from 'lucide-react';
import {Alert, CircularProgress} from '@mui/material';
import {useNavigate, useParams} from 'react-router-dom';
import {PortalShell} from '../../shared/ui';
import {copiarTexto} from '../../shared/utils/clipboard';
import {
    AVISO_BOLETO_EM_REGISTRO,
    AVISO_BOLETO_INDISPONIVEL,
    AVISO_DE_ENCARGOS,
    descreverValorPago,
    formatCompetencia,
    formatDateBR,
    formatDocumento,
    formatMoneyBR,
    formatPeriodo,
    meioDaCobranca,
    pagamentoDaCobranca,
    situacaoDaCobranca,
    situacaoDaFatura,
    tituloDaSecao,
} from './financeiroUtils';
import {useFatura} from './useFatura';

const COPIA_VISIVEL_MS = 2500;

function LinhaDaFatura({item}) {
    return (
        <div className="bf-row-between" style={{alignItems: 'flex-start', padding: '12px 0', borderBottom: '1px solid var(--bf-border)'}}>
            <div style={{flex: 1}}>
                <div style={{fontWeight: 500}}>{item.descricao}</div>
                {item.explicacao ? <div className="bf-muted" style={{fontSize: 14, marginTop: 4}}>{item.explicacao}</div> : null}
            </div>
            <div style={{fontWeight: 600, whiteSpace: 'nowrap'}}>{formatMoneyBR(item.valor)}</div>
        </div>
    );
}

function CampoCopiavel({rotulo, acao, valor, chave, copia, onCopiar}) {
    const desta = copia.chave === chave;
    return (
        <div>
            <div className="bf-meta-label" style={{marginBottom: 8}}>{rotulo}</div>
            <div className="bf-row" style={{gap: 8, alignItems: 'flex-start'}}>
                <code className="bf-code" style={{flex: 1, padding: '10px 12px', whiteSpace: 'pre-wrap', wordBreak: 'break-all'}}>{valor}</code>
                <button className="bf-button bf-button--secondary bf-button--sm" aria-label={acao} onClick={() => onCopiar(chave, valor)}>
                    {desta && copia.ok ? <Check size={16} /> : <Copy size={16} />}
                    <span>Copiar</span>
                </button>
            </div>
            <div role="status" className="bf-hint" style={{marginTop: 4, color: copia.ok ? 'var(--bf-success)' : 'var(--bf-danger)'}}>
                {desta ? (copia.ok ? 'Copiado.' : 'Não foi possível copiar. Selecione o texto e copie.') : null}
            </div>
        </div>
    );
}

function PagamentoDaCobranca({cobranca, copia, onCopiar}) {
    const pagamento = pagamentoDaCobranca(cobranca);

    if (pagamento.tipo === 'BOLETO_EM_REGISTRO') {
        return <div className="bf-note bf-note--warning">{AVISO_BOLETO_EM_REGISTRO}</div>;
    }
    if (pagamento.tipo === 'BOLETO_INDISPONIVEL') {
        return <div className="bf-note bf-note--warning">{AVISO_BOLETO_INDISPONIVEL}</div>;
    }
    if (pagamento.tipo === 'PAGA') {
        const valorPago = descreverValorPago(pagamento);
        return (
            <div className="bf-stack-1" style={{flexDirection: 'column', gap: 4, color: 'var(--bf-success)'}}>
                <div style={{fontWeight: 600}}>{pagamento.pagaEm ? `Paga em ${formatDateBR(pagamento.pagaEm)}` : 'Paga'}</div>
                {valorPago ? <div style={{fontSize: 14}}>Valor pago: {valorPago}</div> : null}
            </div>
        );
    }
    if (pagamento.tipo !== 'BOLETO') return null;

    return (
        <div className="bf-stack-2">
            {pagamento.vencida ? <div className="bf-note bf-note--warning">{AVISO_DE_ENCARGOS}</div> : null}
            {pagamento.linhaDigitavel ? (
                <CampoCopiavel rotulo="Linha digitável" acao="Copiar a linha digitável" valor={pagamento.linhaDigitavel} chave={`${cobranca.id}:linha`} copia={copia} onCopiar={onCopiar} />
            ) : null}
            {pagamento.pixCopiaECola ? (
                <CampoCopiavel rotulo="Pix copia e cola" acao="Copiar o Pix copia e cola" valor={pagamento.pixCopiaECola} chave={`${cobranca.id}:pix`} copia={copia} onCopiar={onCopiar} />
            ) : null}
            {pagamento.urlPdf ? (
                <div>
                    <a className="bf-button bf-button--secondary bf-button--sm" href={pagamento.urlPdf} target="_blank" rel="noopener noreferrer">
                        <ExternalLink size={16} /><span>Abrir boleto (PDF)</span>
                    </a>
                </div>
            ) : null}
        </div>
    );
}

function Cobranca({cobranca, copia, onCopiar}) {
    const situacao = situacaoDaCobranca(cobranca);
    const ehBoleto = cobranca.meio === 'BOLETO';
    return (
        <div className="bf-stack-2" style={{padding: 16, background: 'rgba(241,245,249,0.3)', border: '1px solid var(--bf-border)', borderRadius: 16}} data-testid="cobranca">
            <div className="bf-row-between" style={{alignItems: 'flex-start', flexWrap: 'wrap'}}>
                <div>
                    <div style={{fontWeight: 600}}>{meioDaCobranca(cobranca.meio)}</div>
                    {ehBoleto ? <div className="bf-muted" style={{fontSize: 14, marginTop: 4}}>Vencimento {formatDateBR(cobranca.vencimento)}</div> : null}
                </div>
                <div className="bf-row" style={{gap: 12}}>
                    <span style={{fontWeight: 700}}>{formatMoneyBR(cobranca.valor)}</span>
                    <span className={situacao.className}>{situacao.label}</span>
                </div>
            </div>
            <PagamentoDaCobranca cobranca={cobranca} copia={copia} onCopiar={onCopiar} />
        </div>
    );
}

export default function FaturaPage() {
    const navigate = useNavigate();
    const {faturaId} = useParams();
    const data = useFatura(faturaId);
    const [copia, setCopia] = useState({chave: null, ok: true});
    const timer = useRef(null);

    useEffect(() => () => window.clearTimeout(timer.current), []);

    const handleCopiar = async (chave, valor) => {
        const ok = await copiarTexto(valor);
        setCopia({chave, ok});
        window.clearTimeout(timer.current);
        timer.current = window.setTimeout(() => setCopia({chave: null, ok: true}), COPIA_VISIVEL_MS);
    };

    const fatura = data.detalhe?.fatura;
    const situacao = situacaoDaFatura(fatura?.status);
    const secoes = data.detalhe?.secoes || [];
    const itensSemSecao = data.detalhe?.itensSemSecao || [];

    return (
        <PortalShell>
            <div className="bf-page">
                <div>
                    <button className="bf-button bf-button--ghost bf-button--sm" onClick={() => navigate('/financeiro')} style={{marginBottom: 8}}>
                        <ArrowLeft size={16} /><span>Voltar para as faturas</span>
                    </button>
                    <div className="bf-row" style={{gap: 12, flexWrap: 'wrap'}}>
                        <h1 className="bf-page__title" style={{margin: 0}}>{fatura?.numero ? `Fatura ${fatura.numero}` : 'Fatura'}</h1>
                        {fatura ? <span className={situacao.className}>{situacao.label}</span> : null}
                    </div>
                    {fatura ? <p className="bf-page__subtitle">{fatura.referencia || formatCompetencia(fatura.competencia)}</p> : null}
                </div>

                {data.loading ? <div className="bf-empty"><CircularProgress /></div> : null}
                {!data.loading && data.error ? <Alert severity="warning">{data.error}</Alert> : null}

                {!data.loading && fatura ? (
                    <>
                        <div className="bf-card"><div className="bf-card__body">
                            <div className="bf-row-between" style={{alignItems: 'flex-start', flexWrap: 'wrap', marginBottom: 16}}>
                                <h2 className="bf-section-title bf-row" style={{gap: 8, margin: 0}}><FileText size={18} />Dados da fatura</h2>
                                <button className="bf-button bf-button--primary bf-button--sm" onClick={data.baixarPdf} disabled={data.baixandoPdf}>
                                    {data.baixandoPdf ? <CircularProgress size={16} color="inherit" /> : <Download size={16} />}
                                    <span>Baixar PDF da fatura</span>
                                </button>
                            </div>
                            {data.erroPdf ? <Alert severity="warning" sx={{mb: 2}}>{data.erroPdf}</Alert> : null}
                            <div className="bf-modal-grid">
                                <div>
                                    <div className="bf-meta-label">Empresa</div>
                                    <div className="bf-meta-value">{fatura.empresa}</div>
                                    {fatura.razaoSocial && fatura.razaoSocial !== fatura.empresa ? <div className="bf-muted" style={{fontSize: 14}}>{fatura.razaoSocial}</div> : null}
                                </div>
                                <div><div className="bf-meta-label">CNPJ</div><div className="bf-meta-value">{formatDocumento(fatura.cnpj)}</div></div>
                                <div><div className="bf-meta-label">Emitida em</div><div className="bf-meta-value">{formatDateBR(fatura.emitidaEm)}</div></div>
                                <div><div className="bf-meta-label">Vencimento</div><div className="bf-meta-value">{formatDateBR(fatura.vencimento)}</div></div>
                                <div><div className="bf-meta-label">Competência</div><div className="bf-meta-value">{formatCompetencia(fatura.competencia)}</div></div>
                                {formatPeriodo(fatura.periodoInicio, fatura.periodoFim) ? (
                                    <div><div className="bf-meta-label">Período</div><div className="bf-meta-value">{formatPeriodo(fatura.periodoInicio, fatura.periodoFim)}</div></div>
                                ) : null}
                            </div>
                        </div></div>

                        <div className="bf-card"><div className="bf-card__body">
                            <h2 className="bf-section-title bf-row" style={{gap: 8, margin: '0 0 16px'}}><Receipt size={18} />O que está sendo cobrado</h2>
                            <div className="bf-stack-3">
                                {secoes.map((secao) => (
                                    <section key={secao.id} aria-label={tituloDaSecao(secao)} style={{padding: 16, border: '1px solid var(--bf-border)', borderRadius: 16}}>
                                        <h3 style={{margin: 0, fontSize: 16}}>{tituloDaSecao(secao)}</h3>
                                        {formatPeriodo(secao.periodoInicio, secao.periodoFim) ? <div className="bf-muted" style={{fontSize: 14, marginTop: 4}}>{formatPeriodo(secao.periodoInicio, secao.periodoFim)}</div> : null}
                                        <div style={{marginTop: 8}}>
                                            {secao.itens.map((item) => <LinhaDaFatura key={item.id} item={item} />)}
                                        </div>
                                        <div className="bf-row-between" style={{paddingTop: 12}}>
                                            <span style={{fontWeight: 600}}>Total da assinatura</span>
                                            <span style={{fontWeight: 700}}>{formatMoneyBR(secao.total)}</span>
                                        </div>
                                    </section>
                                ))}
                                {itensSemSecao.length ? (
                                    <section aria-label={secoes.length ? 'Outros itens' : 'Itens da fatura'} style={{padding: 16, border: '1px solid var(--bf-border)', borderRadius: 16}}>
                                        <h3 style={{margin: 0, fontSize: 16}}>{secoes.length ? 'Outros itens' : 'Itens da fatura'}</h3>
                                        <div style={{marginTop: 8}}>
                                            {itensSemSecao.map((item) => <LinhaDaFatura key={item.id} item={item} />)}
                                        </div>
                                    </section>
                                ) : null}
                                <div className="bf-row-between" style={{padding: 16, background: 'rgba(37,99,235,0.05)', borderRadius: 16}}>
                                    <span style={{fontWeight: 600, fontSize: 18}}>Total da fatura</span>
                                    <span style={{fontWeight: 700, fontSize: 24, color: 'var(--bf-primary)'}}>{formatMoneyBR(fatura.valorTotal)}</span>
                                </div>
                            </div>
                        </div></div>

                        <div className="bf-card"><div className="bf-card__body">
                            <h2 className="bf-section-title bf-row" style={{gap: 8, margin: '0 0 16px'}}><Wallet size={18} />Como pagar</h2>
                            {data.erroCobrancas ? <Alert severity="warning">{data.erroCobrancas}</Alert> : null}
                            {!data.erroCobrancas && data.cobrancas.length === 0 ? (
                                <div className="bf-muted">As cobranças desta fatura ainda não foram geradas.</div>
                            ) : null}
                            <div className="bf-stack-2">
                                {data.cobrancas.map((cobranca) => (
                                    <Cobranca key={cobranca.id} cobranca={cobranca} copia={copia} onCopiar={handleCopiar} />
                                ))}
                            </div>
                        </div></div>
                    </>
                ) : null}
            </div>
        </PortalShell>
    );
}
